import { useQuery } from "@tanstack/react-query";
import {
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { Grid3X3 } from "lucide-react";
import { useMemo } from "react";
import { TableCardSkeleton } from "@/components/loading";
import {
  DataTableBandHeader,
  DataTableHeaderRow,
} from "@/components/patterns/table-chrome";
import { SearchActivityOverlay } from "@/components/search-activity-overlay";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@/components/ui/empty";
import { Table, TableBody, TableCell, TableHead, TableRow } from "@/components/ui/table";
import { useIsMobile } from "@/hooks/use-mobile";
import { getDisplayPeriodQuery } from "@/lib/display-period";
import { shouldShowSearchActivityOverlay, type SearchExecutionActivity } from "@/lib/search-activity";
import { useTRPC } from "@/lib/trpc";
import type {
  BrandLlmVisibilityHeatmapCell,
  BrandLlmVisibilityHeatmapData,
  BrandLlmVisibilityHeatmapRow,
} from "@/types/data";
import { cn } from "@/lib/utils";
import type { BrandDashboardPeriod } from "@/state/store/userPreferences";

type HeatmapColumnMeta = {
  align?: "left" | "center";
  sticky?: boolean;
};

const brandColumnMeta: HeatmapColumnMeta = {
  align: "left",
  sticky: true,
};

function formatPercent(value: number): string {
  const resolved = Number.isInteger(value) ? value.toFixed(0) : value.toFixed(1);
  return `${resolved}%`;
}

function getHeatColor(score: number | null) {
  if (score === null) {
    return {
      className: "border-brand-ink/[0.06] bg-brand-ink/[0.02] text-brand-ink/30",
      style: undefined,
    };
  }

  const intensity = Math.max(0, Math.min(score, 100)) / 100;

  // Brand-teal ramp: light teal tint → deep brand-teal (#287a7f ≈ hsl(183,52%,33%))
  const hue = 178 + intensity * 5;
  const saturation = 28 + intensity * 24;
  const lightness = 96 - intensity * 61;

  return {
    className: cn(
      "border-transparent",
      intensity >= 0.52 ? "text-white" : "text-brand-ink/80",
    ),
    style: {
      backgroundColor: `hsl(${hue} ${saturation}% ${lightness}%)`,
    },
  };
}

function HeatmapCellButton({
  brandName,
  cell,
  llmLabel,
  className,
}: {
  brandName: string;
  cell: BrandLlmVisibilityHeatmapCell;
  llmLabel: string;
  className?: string;
}) {
  const heatColor = getHeatColor(cell.visibilityScore);

  return (
    <button
      type="button"
      className={cn(
        "flex h-10 w-full items-center justify-center rounded-md border px-2 text-xs font-semibold tabular-nums transition-all duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-ink/25 hover:scale-[1.03] hover:shadow-sm",
        heatColor.className,
        className,
      )}
      style={heatColor.style}
      aria-label={`${brandName}, ${llmLabel}, visibility score ${cell.visibilityScore === null ? "no eligible data" : formatPercent(cell.visibilityScore)}.`}
    >
      {cell.visibilityScore === null ? "—" : formatPercent(cell.visibilityScore)}
    </button>
  );
}

function BrandRowHeader({ row }: { row: BrandLlmVisibilityHeatmapRow }) {
  return (
    <div className="flex min-w-[160px] items-center gap-2 pr-3 sm:min-w-[180px]">
      {row.brandLogo ? (
        <img
          src={row.brandLogo}
          alt={`${row.brandName} logo`}
          className="size-6 rounded-md border border-brand-ink/[0.06] object-cover"
          onError={(event) => {
            event.currentTarget.style.display = "none";
          }}
        />
      ) : (
        <div className="flex size-6 items-center justify-center rounded-md border border-brand-ink/[0.06] bg-brand-ink/[0.03] text-[9px] font-bold uppercase text-brand-ink/40">
          {row.brandName.slice(0, 2)}
        </div>
      )}
      <div className="min-w-0 flex items-center gap-2">
        <span className="truncate text-[13px] font-semibold text-foreground">{row.brandName}</span>
        {row.isMainBrand ? (
          <Badge variant="secondary" className="rounded-full px-2 py-0 text-[10px] uppercase tracking-[0.12em] shrink-0">
            Main
          </Badge>
        ) : null}
      </div>
    </div>
  );
}

export function BrandLlmVisibilityHeatmapTable({
  activity = null,
  data,
  isLoading = false,
}: {
  activity?: SearchExecutionActivity | null;
  data: BrandLlmVisibilityHeatmapData;
  isLoading?: boolean;
}) {
  const isMobile = useIsMobile();
  const columns = useMemo<ColumnDef<BrandLlmVisibilityHeatmapRow>[]>(() => {
    const dynamicColumns: ColumnDef<BrandLlmVisibilityHeatmapRow>[] = data.llms.map((llm) => ({
      id: llm.id,
      meta: { align: "center" } satisfies HeatmapColumnMeta,
      header: () => (
        <div className="flex min-w-[120px] items-center justify-center gap-2 px-1">
          <div className="flex size-6 shrink-0 items-center justify-center rounded-full bg-white shadow-[0_0_0_1px_rgba(0,0,0,0.04),0_1px_2px_rgba(0,0,0,0.06)]">
            <img
              src={llm.logo}
              alt=""
              aria-hidden="true"
              className="size-4 rounded-sm object-contain"
            />
          </div>
          <div className="min-w-0">
            <div className="truncate text-xs font-semibold text-foreground leading-tight">
              {llm.modelLabel || llm.label}
            </div>
          </div>
        </div>
      ),
      cell: ({ row }) => (
        <HeatmapCellButton
          brandName={row.original.brandName}
          cell={row.original.cells[llm.id]}
          llmLabel={llm.label}
        />
      ),
    }));

    return [
      {
        id: "brand",
        meta: brandColumnMeta,
        header: () => <div className="pl-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-brand-ink/35">Brand</div>,
        cell: ({ row }) => <BrandRowHeader row={row.original} />,
      },
      ...dynamicColumns,
    ];
  }, [data.llms]);

  const table = useReactTable({
    data: data.rows,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const hasHeatmapData = data.llms.length > 0;
  const showLoadingOverlay = shouldShowSearchActivityOverlay(activity, {
    includeScanning: true,
  });

  return (
    <Card className="gap-0 overflow-hidden rounded-xl border p-0 shadow-sm" data-testid="brand-llm-visibility-heatmap">
      <div className="flex h-12 items-center border-b px-5 shrink-0">
        <span className="text-[15px] font-semibold">Visibility by LLM</span>
      </div>
      <CardContent className="relative px-0">
        {isLoading ? (
          <div className="px-6 py-5">
            <TableCardSkeleton rows={6} columns={4} />
          </div>
        ) : !hasHeatmapData ? (
          <div className="p-6">
            <Empty className="rounded-xl border-brand-ink/12 bg-brand-ink/[0.015] py-14">
              <EmptyHeader>
                <EmptyMedia variant="icon">
                  <Grid3X3 className="size-5" />
                </EmptyMedia>
                <EmptyTitle>No LLM responses in this period</EmptyTitle>
                <EmptyDescription>
                  Run more searches or widen the dashboard period to populate the visibility heatmap.
                </EmptyDescription>
              </EmptyHeader>
            </Empty>
          </div>
        ) : isMobile ? (
          <div className="space-y-3 px-4 py-4 sm:px-5">
            {data.rows.map((row) => (
              <div
                key={row.brandName}
                className="rounded-lg border border-brand-ink/[0.08] bg-background p-3 shadow-sm"
              >
                <BrandRowHeader row={row} />
                <div className="mt-3 grid grid-cols-1 gap-2 min-[420px]:grid-cols-2">
                  {data.llms.map((llm) => (
                    <div
                      key={`${row.brandName}-${llm.id}`}
                      className="flex items-center justify-between gap-3 rounded-md border border-brand-ink/[0.06] bg-brand-ink/[0.015] px-3 py-2"
                    >
                      <div className="flex min-w-0 items-center gap-2">
                        <div className="flex size-6 shrink-0 items-center justify-center rounded-full bg-white shadow-[0_0_0_1px_rgba(0,0,0,0.04),0_1px_2px_rgba(0,0,0,0.06)]">
                          <img
                            src={llm.logo}
                            alt=""
                            aria-hidden="true"
                            className="size-4 rounded-sm object-contain"
                          />
                        </div>
                        <span className="min-w-0 truncate text-xs font-semibold text-foreground">
                          {llm.modelLabel || llm.label}
                        </span>
                      </div>
                      <HeatmapCellButton
                        brandName={row.brandName}
                        cell={row.cells[llm.id]}
                        llmLabel={llm.label}
                        className="h-8 w-auto min-w-[4.5rem] shrink-0 px-3"
                      />
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="overflow-x-auto px-5 py-4">
            <Table className="min-w-max border-separate border-spacing-x-1 border-spacing-y-0.5">
              <DataTableBandHeader className="bg-transparent">
                {table.getHeaderGroups().map((headerGroup) => (
                  <DataTableHeaderRow key={headerGroup.id} className="border-transparent hover:bg-transparent">
                    {headerGroup.headers.map((header) => {
                      const meta = (header.column.columnDef.meta ?? {}) as HeatmapColumnMeta;
                      const isSticky = meta.sticky;

                      return (
                        <TableHead
                          key={header.id}
                          className={cn(
                            "h-auto border-b-0 bg-transparent px-1 py-1.5 align-bottom",
                            meta.align === "center" ? "text-center" : "text-left",
                            isSticky && "sticky left-0 z-20 bg-card",
                          )}
                        >
                          {header.isPlaceholder
                            ? null
                            : flexRender(header.column.columnDef.header, header.getContext())}
                        </TableHead>
                      );
                    })}
                  </DataTableHeaderRow>
                ))}
              </DataTableBandHeader>
              <TableBody>
                {table.getRowModel().rows.map((row) => (
                  <TableRow key={row.id} className="group border-transparent hover:bg-transparent">
                    {row.getVisibleCells().map((cell) => {
                      const meta = (cell.column.columnDef.meta ?? {}) as HeatmapColumnMeta;
                      const isSticky = meta.sticky;

                      return (
                        <TableCell
                          key={cell.id}
                          className={cn(
                            "px-1 py-0 align-middle",
                            isSticky && "sticky left-0 z-10 bg-card",
                          )}
                        >
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
        {showLoadingOverlay && <SearchActivityOverlay activity={activity} showElapsedTime />}
      </CardContent>
    </Card>
  );
}

export function BrandLlmVisibilityHeatmap({
  activity = null,
  brandId,
  period,
}: {
  brandId: string;
  period: BrandDashboardPeriod;
  activity?: SearchExecutionActivity | null;
}) {
  const trpc = useTRPC();
  const { data, isLoading } = useQuery(
    trpc.charts.getBrandLlmVisibilityHeatmapData.queryOptions({
      brandId,
      ...getDisplayPeriodQuery(period),
    }),
  );

  return (
    <BrandLlmVisibilityHeatmapTable
      activity={activity}
      data={data ?? { rows: [], llms: [] }}
      isLoading={isLoading}
    />
  );
}
